/* eslint-disable class-methods-use-this */
import { slideToggle } from './utils';

class EmPanels {
	/**
	 * Run the setup() function, passing along the options.
	 * @param  {Object} options Object literal containing option overrides.
	 */
	constructor() {
		this.options = {
			selectors: {
				container: 'em-panels',
				tabsModifier: 'em-panels--tabs',
				accordionsModifier: 'em-panels--accordions',
				control: 'em-panel__control',
				controlActive: 'active',
				panel: 'em-panel__panel',
				panelActive: 'active',
				nav: 'em-panels__nav',
				controls: 'em-panels__controls',
				autoClose: 'em-panels--autoclose',
			},
			tabsToAccordions: true,
			accordionsToTabs: false,
		};

		this.selectors = this.options.selectors;

		this.containers = [...document.querySelectorAll(`.${this.selectors.container}`)];
		this.containers.forEach((container) => {
			if (container.classList.contains(this.selectors.tabsModifier)) {
				if (this.options.tabsToAccordions) {
					container.dataset.toggle = true;
				}
			} else if (container.classList.contains(this.selectors.accordionsModifier)) {
				if (this.options.accordionsToTabs) {
					container.dataset.toggle = true;
				}
			}
		});

		this.controls = [...document.querySelectorAll(`.${this.selectors.control}`)];
		this.controls.forEach((control) => {
			control.addEventListener('click', (e) => {
				this.handleControlClick(e);
			});
		});

		// window.addEventListener('resize', () => {
		// 	window.requestAnimationFrame(() => {
		// 		this.handlePanelsLayout();
		// 	});
		// });
		// window.addEventListener('EmagineBlocks:loaded', this.handlePanelsLayout);
	}

	/**
	 * Given a control element, get it's corresponding panel element, and the active control and panel.
	 * @param  {Node} control	Target control element.
	 * @return {Object}         Object containing target control and panel, as well as active control and panel, if available.
	 */
	getPanelElements = (control) => {
		const container = control.closest(`.${this.selectors.container}`);
		const activeControls = [...container.querySelectorAll(`.${this.selectors.control}.${this.selectors.controlActive}`)];
		return {
			container,
			target: {
				container,
				controls: [...container.querySelectorAll(`[aria-controls=${control.getAttribute('aria-controls')}]`)],
				panel: document.getElementById(control.getAttribute('aria-controls')),
			},
			active: {
				container,
				controls: activeControls,
				panel: activeControls.length > 0 ? document.getElementById(activeControls[0].getAttribute('aria-controls')) : null,
			},
		};
	}

	/**
	 * Toggle the selectors and aria attributes of the control and panel.
	 * @param  {Object} panelItem Object containing the control and panel to toggle.
	 */
	togglePanel = ({ container, panel, controls }) => {
		if (!panel || !controls || !container) {
			return;
		}

		const { panelType } = container.dataset;

		// Toggle panel selectors and aria attribues.
		panel.classList.toggle(this.selectors.panelActive);

		// Slide the accordion content open or closed depending on it's current state.
		slideToggle(panel);

		// Toggle control selectors and aria attributes.
		controls.forEach((control) => {
			control.classList.toggle(this.selectors.controlActive);
			if (panelType === 'tabs') {
				/**
				 * Toggle the w3.org recommended attributes on the tab controls.
				 * @link https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
				 */
				const isSelected = control.getAttribute('aria-selected') === 'true';
				control.setAttribute('aria-selected', !isSelected);

				if (isSelected) {
					control.removeAttribute('tab-index');
				} else {
					control.setAttribute('tab-index', '-1');
				}
			} else if (panelType === 'accordions') {
				/**
				 * Toggle the w3.org recommended attributes on the accordion controls.
				 * @link https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
				 */
				const isSelected = control.getAttribute('aria-expanded') === 'true';
				control.setAttribute('aria-expanded', !isSelected);
			}
		});
	}

	/**
	 * Tab click-event handler.
	 * @param  {Event} e
	 */
	handleControlClick = (e) => {
		/**
		 * Use the event target to get the panel control.
		 * @type {Object}
		 */
		const control = e.target.closest(`.${this.selectors.control}`);
		if (control) {
			/**
			 * Get the target control and panel.
			 * @type {Object}
			 */
			const els = this.getPanelElements(control);

			/**
			 * Check if the target panel is also the active panel.
			 * @type {Boolean}
			 */
			const isActive = els.target.controls[0] === els.active.controls[0];

			/**
			 * Check if container contains an active panel.
			 * @type {Boolean}
			 */
			const containerHasActive = els.active.controls.length > 0;

			/**
			 * If the container is an accordion, it can toggle itself, otherwise, it can't.
			 * @type {Boolean}
			 */
			const canToggleSelf = !!els.container.classList.contains(this.selectors.accordionsModifier);

			if (isActive && canToggleSelf) {
				// Force toggle if the target is active and canToggleSelf is true.
				this.togglePanel(els.target);
			} else if (!isActive || canToggleSelf) {
				// Check if another panel is active, and if so toggle it...
				if (els.container.classList.contains(this.selectors.autoClose) && containerHasActive) {
					this.togglePanel(els.active);
				}

				// ...and then toggle the target.
				this.togglePanel(els.target);
			}
		}
	}

	/**
	 * Toggle between accordions and tabs.
	 * @param  {Node} 	container 	Element to toggle
	 * @param  {String} oldClass  	Class to remove
	 * @param  {String} newClass  	Class to add
	 */
	togglePanelsLayout(container, oldClass, newClass) {
		container.classList.remove(oldClass);
		container.classList.add(newClass);
	}

	/**
	 * Update panels layout based on screen size.
	 */
	// handlePanelsLayout() {
	// 	/**
	// 	 * Get panels instance from global window variable.
	// 	 * @type {Object}
	// 	 */
	// 	const panels = window.EmagineBlocks.components.panels.instance;

	// 	/**
	// 	 * Shorter alias for panels.options.selectors
	// 	 * @type {Object}
	// 	 */
	// 	const { selectors } = panels.options;

	// 	// Loop through each container and check if it should toggle based on screensize.
	// 	panels.containers.forEach((container) => {
	// 		// Make sure that we actually want to toggle the layout for this container.
	// 		if (container.dataset.toggle === 'true') {
	// 			const nav = container.querySelector(`.${selectors.nav}`);
	// 			const controls = container.querySelector(`.${selectors.controls}`);

	// 			// Check if controls is wider than nav, or not, and trigger the layout toggle.
	// 			if (controls.clientWidth > nav.clientWidth) {
	// 				panels.togglePanelsLayout(container, selectors.tabsModifier, selectors.accordionsModifier);
	// 			} else {
	// 				panels.togglePanelsLayout(container, selectors.accordionsModifier, selectors.tabsModifier);
	// 			}
	// 		}
	// 	});
	// }
}

// eslint-disable-next-line no-unused-vars
const panels = new EmPanels();
